<template>
  <component
    :is="labelType"
    v-if="label || placeholder"
    :id="labelId"
    :for="labelType === 'label' ? forId : null"
    :class="_class"
    ><span
      v-if="allowHtml"
      :id="labelId + '-text'"
      class="label-text"
      role="presentation"
      v-html="labelText"
    ></span
    ><span
      v-else
      :id="labelId + '-text'"
      class="label-text"
      role="presentation"
      >{{ labelText }}</span
    ><span
      v-if="label && placeholder"
      :id="labelId + '-offscreen-label'"
      class="offscreen-label screen-reader-text"
      role="presentation"
      >{{ placeholder }}</span
    ><span
      v-if="
        showLabelRequired &&
        displayRequired &&
        !optional &&
        !readonly &&
        labelText !== ''
      "
      class="required-text"
      :aria-label="requiredOffscreenText"
      role="presentation"
      >&nbsp;{{ resolveTxt("skin.label.required", "*") }}</span
    ><span
      v-if="
        showLabelOptional &&
        displayOptional &&
        optional &&
        !readonly &&
        labelText !== ''
      "
      class="optional-text"
      :aria-label="optionalOffscreenText"
      role="presentation"
      >&nbsp;{{ resolveTxt("skin.label.optional", "(Optional)") }}</span
    ></component
  >
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatLabel",
  props: {
    labelId: {
      type: String,
      required: true,
      description: "Label id."
    },
    forId: {
      type: [String],
      description: "Specifies which form element a label is bound to."
    },
    fieldset: {
      type: Boolean,
      default: false,
      description:
        "Used to group related elements in a form. Draws a box around the related elements."
    },
    label: {
      type: [String],
      description: "Specifies label text."
    },
    allowHtml: {
      type: Boolean,
      default: false,
      description: "If true allows html to render on page."
    },
    placeholder: {
      type: [String],
      description: "Specifies text for the screen reader."
    },
    optional: {
      type: Boolean,
      default: true,
      description: "Makes the label optional (not compulsory)."
    },
    readonly: {
      type: [Boolean],
      description: "If true, required or optional property wont be displayed."
    },
    displayOptional: {
      type: Boolean,
      default: true,
      description: "Property to override the injected showLabelOptional flag."
    },
    displayRequired: {
      type: Boolean,
      default: true,
      description: "Property to override the injected showLabelRequired flag."
    },
    inline: {
      type: Boolean,
      default: false,
      description:
        "Flag to indicate if the label should display as inline-block. If not set the label is displayed as block."
    },
    errors: [Array]
  },
  data: function () {
    const params = this.displayParams ? this.displayParams : false;
    const nonceMatch = (
      document.querySelector(
        'meta[http-equiv="Content-Security-Policy"]'
      ) as any
    )?.content?.match(/style-src\s+[^;]*'nonce-([^']*)'/);
    const cspNonce = nonceMatch?.length > 1 ? nonceMatch[1] : undefined;
    return {
      showLabelOptional:
        params && params.showLabelOptional !== undefined
          ? params.showLabelOptional
          : true,
      showLabelRequired:
        params && params.showLabelRequired !== undefined
          ? params.showLabelRequired
          : false,
      suppressOffscreenOptional:
        params && params.suppressOffscreenOptional !== undefined
          ? params.suppressOffscreenOptional
          : false,
      suppressOffscreenRequired:
        params && params.suppressOffscreenRequired !== undefined
          ? params.suppressOffscreenRequired
          : true,
      cspNonce
    };
  },
  computed: {
    _class: function () {
      return {
        "cat-label": true,
        "inline-block": this.inline,
        "screen-reader-text": !this.label
      };
    },
    labelType: function () {
      return this.fieldset ? "legend" : "label";
    },
    labelText: function () {
      if (this.label) {
        const withHeadNonce = (html?: string) =>
          this.cspNonce
            ? // Add nonce to style tag if it doesn't exist or update it to the page one if it exists.
              html
                ?.replaceAll(
                  /<style(?![^>]*\bnonce\b)([^>]*)>/g,
                  `<style$1 nonce="${this.cspNonce}">`
                )
                ?.replaceAll(
                  /\s+nonce=['"][^'"]*['"]/g,
                  ` nonce="${this.cspNonce}"`
                )
            : html;
        return this.allowHtml ? withHeadNonce(this.label) : this.label;
      } else if (this.placeholder) {
        return this.placeholder;
      } else {
        return "";
      }
    },
    optionalOffscreenText: function () {
      return !this.suppressOffscreenOptional &&
        this.showLabelOptional &&
        this.optional
        ? this.resolveTxt("skin.label.optional.offscreen", "Optional")
        : null;
    },
    requiredOffscreenText: function () {
      return !this.suppressOffscreenRequired &&
        this.showLabelRequired &&
        !this.optional
        ? this.resolveTxt("skin.label.required.offscreen", "Required")
        : null;
    }
  }
});
</script>
